import { useEffect, useState } from 'react';

import {
  usePayoutAddressValidation,
  KIND_NAMES,
  type PayoutState,
} from '../hooks/usePayoutAddressValidation';

/**
 * The BTC payout address.
 *
 * The most consequential field on the page, and styled to say so. Mined BTC is paid by
 * the network directly to this address; Metered Mining never holds it, so a wrong
 * address cannot be corrected or refunded by anyone.
 *
 * Validation is performed BY THE SERVER, using the same validator order creation uses.
 * The browser carries no Bitcoin address implementation of its own -- a second one would
 * drift from the authoritative rules, and a drift here either rejects a good address or,
 * far worse, reassures someone about a bad one.
 *
 * THE VALIDATION ITSELF NOW LIVES IN `hooks/usePayoutAddressValidation`, unchanged, so
 * the per-order editor and the account default wallet reach the same verdict about the
 * same address. This component is the configurator's PRESENTATION of that verdict; the
 * rule is no longer its private property.
 *
 * Feedback here is advisory. The server revalidates at order creation regardless.
 *
 * PowerPool IDs are not accepted: their format is unknown, so there is no way to tell a
 * real one from a typo. The standing "PowerPool ID support is coming soon" line was
 * removed at the owner's request -- it advertised a roadmap item beside the field where
 * a customer is trying to complete an order. Nothing about the RULE changed: the server
 * still refuses anything that is not a valid Bitcoin address, and its rejection says so.
 */

export type { PayoutState };

interface PayoutAddressFieldProps {
  value: string;
  onChange: (value: string) => void;
  onStateChange: (state: PayoutState) => void;
}

export default function PayoutAddressField({
  value,
  onChange,
  onStateChange,
}: PayoutAddressFieldProps) {
  const [touched, setTouched] = useState(false);

  // One shared verdict, three screens. See hooks/usePayoutAddressValidation.
  const { state } = usePayoutAddressValidation(value);

  /*
   * The parent still needs the verdict to gate order creation, and still recreates its
   * callback every render -- so this reports on state CHANGE rather than being folded
   * into the hook, exactly as before.
   */
  useEffect(() => {
    onStateChange(state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
  const showError = touched && state.status === 'invalid';
  const showValid = state.status === 'valid';

  return (
    <div>
      <label htmlFor="payout-address" className="mm-label">
        Your Bitcoin payout address
      </label>

      {/*
        THE CUSTODY SENTENCE WAS REMOVED FROM HERE ON BERTRAND'S EXPLICIT INSTRUCTION,
        after the conflict was raised and confirmed. It read: "It never passes through
        us, so make sure it is one you control."

        RECORD WHAT THAT COST, because it is not visible from this file. That sentence
        was moved OUT of the hero on the basis that this field carried it, so the
        configurator now states non-custody nowhere: the remaining statements are the
        order status page (after purchase) and the footer's "Non-custodial" marker. It
        also carried the only prompt to check the address, which Terms 3.2 leans on --
        a reward sent to a wrong address "is treated as properly delivered".

        Nothing about the RULE changed. The payout address is still required up front,
        still validated server-side, and mined BTC still never touches Metered Mining.
      */}
      <p className="mm-help mt-1.5">
        If your shares solve a block, the Bitcoin network pays the reward to this address.
      </p>

      <input
        id="payout-address"
        name="payoutAddress"
        type="text"
        inputMode="text"
        autoComplete="off"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={() => setTouched(true)}
        placeholder="bc1…"
        aria-invalid={showError}
        aria-describedby="payout-address-status"
        data-state={showError ? 'invalid' : showValid ? 'valid' : 'neutral'}
        // 16px on mobile deliberately: iOS Safari zooms the whole page when a focused
        // input is under 16px, which is jarring on the most important field on the page.
        // The smaller monospace size returns from sm upward, where no zoom occurs.
        className="mm-input mt-3 font-mono text-base sm:text-sm"
      />

      <p
        id="payout-address-status"
        role="status"
        aria-live="polite"
        data-testid="payout-status"
        // The reserved line is what stops a validation message appearing and
        // disappearing from resizing the card. 1.25rem is one line at this size --
        // the previous 1.5rem reserved more than the text it holds.
        className="mt-1.5 min-h-[1.25rem] text-sm"
        style={{
          color: showError
            ? 'var(--mm-danger)'
            : showValid
              ? 'var(--mm-success)'
              : 'var(--mm-text-subtle)',
        }}
      >
        {state.status === 'checking' && 'Checking address…'}
        {showValid && (
          <>
            <span aria-hidden="true">✓ </span>
            Valid {KIND_NAMES[(state as { kind: string }).kind] ?? (state as { kind: string }).kind}{' '}
            address.
          </>
        )}
        {showError && (state as { reason: string }).reason}
      </p>
    </div>
  );
}
